import React from "react";
import { map } from "lodash";
import { useTranslation } from "react-i18next";
import TaskCard from "./TaskCard";
import {
  DeleteTaskModalState,
  TaskData,
  TaskModalState
} from "@/entities/tasks/types";
import { UserData } from "@/entities/users/types";
import { Button } from "@/components/ui/button";
import EmptyTasksState from "@/components/layouts/TasksDrawer/EmptyTasksState";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import { TaskAnimationState } from "@/entities/tasks/useTasksListData";
import { useElementWidth } from "@/hooks/useElementWidth";

// Card width thresholds mirror the previous md/xl viewport breakpoints, but
// are measured against this grid's own container so the column count still
// reacts to a sidebar collapse/expand, which changes content width without
// changing the viewport.
const TWO_COLUMN_MIN_WIDTH = 640;
const THREE_COLUMN_MIN_WIDTH = 960;

const getColumnCountClass = (width: number): string => {
  if (width >= THREE_COLUMN_MIN_WIDTH) return "grid-cols-3";
  if (width >= TWO_COLUMN_MIN_WIDTH) return "grid-cols-2";
  return "grid-cols-1";
};

interface Props {
  tasks: TaskData[];
  selectedTab: string;
  userId: string | undefined;
  users: UserData[];
  taskAnimationStates: TaskAnimationState;
  onCheckedChange: (taskId: string, checked: boolean) => void;
  setTaskModalState: React.Dispatch<React.SetStateAction<TaskModalState>>;
  setDeleteTaskModalState: React.Dispatch<
    React.SetStateAction<DeleteTaskModalState>
  >;
  isSearching?: boolean;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  height?: string;
}

const TaskCardsGrid: React.FC<Props> = ({
  tasks,
  selectedTab,
  userId,
  users,
  taskAnimationStates,
  onCheckedChange,
  setTaskModalState,
  setDeleteTaskModalState,
  isSearching = false,
  isLoading = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  height
}) => {
  const { t } = useTranslation();
  const { ref: containerRef, width: containerWidth } =
    useElementWidth<HTMLDivElement>();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-72">
        <Spinner size="lg" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return isSearching ? (
      <div className="flex items-center justify-center h-72">
        <Text variant="bodySecondary">{t("noResultsFound")}</Text>
      </div>
    ) : (
      <EmptyTasksState className="h-72" />
    );
  }

  return (
    <div
      ref={containerRef}
      className="overflow-y-auto p-4"
      style={{ height }}
    >
      <div
        data-test="task-cards-grid"
        className={`grid ${getColumnCountClass(containerWidth)} gap-5`}
      >
        {map(tasks, (task) => {
          const animationState = taskAnimationStates[task.id];
          const isCompleted =
            animationState?.localIsCompleted ?? task.isCompleted;
          const showStrikethrough = animationState?.showStrikethrough ?? false;
          const isAnimating = animationState?.isAnimating ?? false;

          return (
            <TaskCard
              key={task.id}
              task={task}
              selectedTab={selectedTab}
              userId={userId}
              users={users}
              isCompleted={isCompleted}
              showStrikethrough={showStrikethrough}
              isAnimating={isAnimating}
              onCheckedChange={(checked) => onCheckedChange(task.id, checked)}
              setTaskModalState={setTaskModalState}
              setDeleteTaskModalState={setDeleteTaskModalState}
            />
          );
        })}
      </div>
      {hasNextPage && (
        <div className="flex justify-center pt-4">
          <Button
            variant="tertiary"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {t("loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TaskCardsGrid;
